// ============================================================================
//  Price tier resolver (Feb 2026)
// ============================================================================
//
//  A product carries up to 4 optional price tiers plus the legacy `price`
//  field, and the storefront picks which one to show based on WHO is looking:
//
//    price_public    -> anonymous / customer shoppers on the public catalog
//    price_store     -> a logged-in store account (rol = 'store')
//    price_wholesale -> shown when the cart subtotal ≥ WHOLESALE_THRESHOLD_USD
//    price_special   -> VIP/negotiated tier — flagged on the user with
//                        `price_tier === 'special'` (any role can carry it)
//
//  Whenever the resolved tier is null/undefined we fall back to the base
//  `price` — this keeps existing products working while stores populate the
//  new fields.
//
//  IMPORTANT: this is the *display* price only. Checkout still recomputes
//  server-side (POST /orders re-prices every line item from the database).
// ----------------------------------------------------------------------------

const WHOLESALE_THRESHOLD_USD = 300;

/**
 * Pick the price tier a given user should see on the catalog card / PDP.
 * Returns { price, tier, tierLabel } — never null.
 */
export function resolveEffectivePrice(product, viewer, cartSubtotal = 0) {
  if (!product) return { price: 0, tier: 'public', tierLabel: 'Público' };
  const p = (k) => (product[k] != null && Number.isFinite(+product[k]) ? +product[k] : null);

  // 1. VIP / negotiated — highest priority, overrides everything else.
  if (viewer?.price_tier === 'special' && p('price_special') != null) {
    return { price: p('price_special'), tier: 'special', tierLabel: 'Especial' };
  }

  // 2. Wholesale — trigger only when the current cart (or per-line multiplier)
  //    would already cross the threshold, so shoppers don't see it prematurely.
  if (cartSubtotal >= WHOLESALE_THRESHOLD_USD && p('price_wholesale') != null) {
    return { price: p('price_wholesale'), tier: 'wholesale', tierLabel: 'Mayorista' };
  }

  // 3. Store — logged-in physical branch account.
  if (viewer?.role === 'store' && p('price_store') != null) {
    return { price: p('price_store'), tier: 'store', tierLabel: 'Tienda' };
  }

  // 4. Public.
  if (p('price_public') != null) {
    return { price: p('price_public'), tier: 'public', tierLabel: 'Público' };
  }

  // 5. Legacy fallback.
  return { price: +product.price || 0, tier: 'public', tierLabel: 'Público' };
}

export const PRICE_TIERS = [
  { key: 'price_public',    label: 'Público',    hint: 'Cliente anónimo / consumidor final' },
  { key: 'price_store',     label: 'Tienda',     hint: 'Cuenta de sucursal logueada' },
  { key: 'price_wholesale', label: 'Mayorista',  hint: `Se aplica al superar $${WHOLESALE_THRESHOLD_USD} en el carrito` },
  { key: 'price_special',   label: 'Especial',   hint: 'VIP — usuarios con price_tier = "special"' },
];
